import { IContactFormData } from 'src/SharedCustomHooks/UseContactFormData/types';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  IContactFormDataForAPI,
  IContactPayloadForAPI,
} from 'src/utils/CommonHelpers/ContactAPIOperations/types';
import { toast } from 'react-toastify';
import { addExistingHomeownerToSuite } from 'src/services/client';
import {
  handleImageUpload,
  transformContactFormDataForAPI,
} from 'src/utils/CommonHelpers/ContactAPIOperations/commonContactDataHelpers';
import { buildSuitesPayload } from 'src/pages/SharedPageCustomHooks/UseContactSaveSuitesPage/helpers';
import {
  useContactAPI,
  UseContactAPIReturn,
} from 'src/SharedCustomHooks/UseContactAPI/useContactAPI';
import {
  useUserManagementAPI,
  UseUserManagementAPIReturn,
} from 'src/SharedCustomHooks/UseUserManagementAPI/useUserManagementAPI';

export interface UseContactAPIOperationsSuitesReturn {
  isLoading: boolean;
  response: any;
  error: string | null;

  onCreateContact: (
    formData: IContactFormData,
    propertyId: string,
    suiteId: string
  ) => Promise<boolean>;

  onEditContact: (
    formData: IContactFormData,
    propertyId: string,
    suiteId: string
  ) => Promise<boolean>;

  addExistingUserToSuite: (
    formData: IContactFormData,
    propertyId: string,
    suiteId: string
  ) => Promise<boolean>;

  clearResponse: () => void;
}

export const useContactAPIOperationsSuites = (): UseContactAPIOperationsSuitesReturn => {
  const contactAPI: UseContactAPIReturn = useContactAPI();
  const userManagementAPI: UseUserManagementAPIReturn = useUserManagementAPI();

  const adminUser: any = useSelector(selectAuthenticatedUser);

  const onCreateContact = async (
    formData: IContactFormData,
    propertyId: string,
    suiteId: string
  ): Promise<boolean> => {
    try {
      const imageUrl: string = await handleImageUpload(formData);
      const apiFormData: IContactFormDataForAPI =
        transformContactFormDataForAPI(formData, imageUrl);

      const payload: IContactPayloadForAPI = buildSuitesPayload(
        apiFormData,
        propertyId,
        suiteId,
        adminUser,
        userManagementAPI.allRoles
      );

      const { ok } = await contactAPI.saveContact(payload);
      return ok;
    } catch (err: any) {
      console.error('Contact creation failed:', err);
      return false;
    }
  };

  const onEditContact = async (
    formData: IContactFormData,
    propertyId: string,
    suiteId: string
  ): Promise<boolean> => {
    if (!formData.id) {
      const error = 'Cannot edit contact: Missing contact ID';
      console.error(error);
      toast.error(`🚫 ${error}`, {
        autoClose: 8000,
        position: 'top-center',
      });
      return false;
    }

    try {
      const imageUrl: string = await handleImageUpload(formData);
      const apiFormData: IContactFormDataForAPI =
        transformContactFormDataForAPI(formData, imageUrl);

      const payload: IContactPayloadForAPI = buildSuitesPayload(
        apiFormData,
        propertyId,
        suiteId,
        adminUser,
        userManagementAPI.allRoles
      );

      const { ok } = await contactAPI.updateContact(payload);
      return ok;
    } catch (err: any) {
      console.error('Contact update failed:', err);
      return false;
    }
  };

  const addExistingUserToSuite = async (
    formData: IContactFormData,
    propertyId: string,
    suiteId: string
  ): Promise<boolean> => {
    const requestPayload = {
      SuiteId: suiteId,
      PropertyId: propertyId,
      Role: formData.userRole,
      UserId: formData.id,
      IsPrimaryContact: formData.isPrimaryContact,
      CreatedBy: adminUser.id,
    };

    try {
      const response: any = await addExistingHomeownerToSuite(requestPayload);

      if (response.status === 200 || response.ok) {
        const responseJson = await response.json();

        if (responseJson.statusCode === 200) {
          toast.success('ⓘ User added to suite successfully!', {
            autoClose: 10000,
            position: 'top-center',
          });
          return true;
        } else {
          throw new Error(
            `API returned status code: ${responseJson.statusCode}`
          );
        }
      } else {
        throw new Error(`Request failed with status: ${response.status}`);
      }
    } catch (err: any) {
      console.error('Adding existing user to suite failed:', err);
      const errorMessage =
        err.message || 'Something went wrong, please try again.';
      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
      throw err;
    }
  };

  return {
    isLoading: contactAPI.isSaving,
    response: contactAPI.lastResponse,
    error: contactAPI.error,
    onCreateContact,
    onEditContact,
    addExistingUserToSuite,
    clearResponse: contactAPI.clearResponse,
  };
};
